// UXV.F3 (#1424) — "Import token refinements" modal.
//
// Closes the design-token round-trip from the UI: the user pastes or uploads
// the edited `tokens.json` from their Claude Design export, previews the diff
// against the current design_tokens artefact (server-side, zero writes), reviews
// per-change accept/reject in <TokenDiffReview>, and applies — which the API
// commits as a new artefact version (PM/TECH_LEAD only).
//
// Error handling: every non-2xx from preview or apply is surfaced inline and the
// modal stays open. 403 (role denied) gets friendlier copy; 400 (bad body) /
// 404 / 409 (stale hash or all-excluded) fall back to the server's message.
// A client-side JSON.parse failure is caught before the preview call.

import { useState, type ChangeEvent } from 'react';
import { apiFetch, ApiError } from '../lib/api';
import TokenDiffReview, { type TokenDiffReport } from './TokenDiffReview';

interface Props {
  projectId: string;
  onClose: () => void;
  /** Fired after a successful apply so the caller can refresh the artefact. */
  onApplied: () => void;
}

interface PreviewResponse {
  diff: TokenDiffReport;
}

export default function TokenReimportModal({ projectId, onClose, onApplied }: Props) {
  const [raw, setRaw] = useState('');
  const [tokens, setTokens] = useState<Record<string, unknown> | null>(null);
  const [diff, setDiff] = useState<TokenDiffReport | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      setRaw(await file.text());
    } catch {
      setError('Could not read that file. Try pasting the JSON instead.');
    }
  }

  async function handlePreview() {
    setError('');
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      setError("That doesn't look like valid JSON. Paste or upload the tokens.json from your Claude Design export.");
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setError('Expected a design-tokens object — the tokens.json from your Claude Design export.');
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch<PreviewResponse>(
        `/projects/${projectId}/design-tokens/reimport`,
        { method: 'POST', body: JSON.stringify({ tokens: parsed }) },
      );
      setTokens(parsed as Record<string, unknown>);
      setDiff(res.diff);
    } catch (err) {
      // Preview is read-only and gated by requireTenantMember — a 403 here is a
      // tenant-membership problem, NOT the PM/TECH_LEAD apply gate, so surface
      // the server's own message rather than the apply-denied copy.
      setError(describeError(err, false));
    } finally {
      setBusy(false);
    }
  }

  async function handleApply(excludePaths: string[]) {
    if (!tokens || !diff) return;
    setApplying(true);
    setError('');
    try {
      await apiFetch(
        `/projects/${projectId}/design-tokens/reimport/apply`,
        { method: 'POST', body: JSON.stringify({ tokens, expectedDiffHash: diff.diffHash, excludePaths }) },
      );
      onApplied();
    } catch (err) {
      setError(describeError(err, true));
    } finally {
      setApplying(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Import token refinements"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)', padding: '48px 16px', overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 640, background: 'var(--bg)',
          borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.3)', padding: 20,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
            Import token refinements
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', fontSize: 20, lineHeight: 1, color: 'var(--text-secondary)', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>

        {!diff ? (
          <div>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
              Paste or upload the edited <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>tokens.json</code> from
              your Claude Design export. You will review each change before anything is committed.
            </p>
            <input
              type="file"
              accept=".json,application/json"
              onChange={handleFile}
              aria-label="Upload tokens.json"
              style={{ fontFamily: 'var(--font-body)', fontSize: 13, marginBottom: 10, display: 'block' }}
            />
            <textarea
              value={raw}
              onChange={e => setRaw(e.target.value)}
              aria-label="Tokens JSON"
              placeholder={'{\n  "colors": { "primary": { "DEFAULT": "#7C3AED" } },\n  ...\n}'}
              rows={10}
              style={{
                width: '100%', fontFamily: 'var(--font-mono)', fontSize: 12,
                padding: 10, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                background: 'var(--surface)', color: 'var(--text)', resize: 'vertical', boxSizing: 'border-box',
              }}
            />
            {error && (
              <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', marginTop: 8 }}>{error}</p>
            )}
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                style={{ fontFamily: 'var(--font-body)', fontSize: 13, padding: '6px 14px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handlePreview()}
                disabled={busy || raw.trim().length === 0}
                style={{
                  fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, padding: '6px 14px',
                  color: 'white', border: 'none', borderRadius: 'var(--radius-sm)',
                  background: busy || raw.trim().length === 0 ? 'var(--text-secondary)' : 'var(--primary)',
                  cursor: busy || raw.trim().length === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                {busy ? 'Previewing…' : 'Preview changes'}
              </button>
            </div>
          </div>
        ) : (
          <div>
            {error && (
              <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', margin: '0 0 12px' }}>{error}</p>
            )}
            <TokenDiffReview diff={diff} onApply={handleApply} onCancel={onClose} applying={applying} />
          </div>
        )}
      </div>
    </div>
  );
}

function describeError(err: unknown, isApply: boolean): string {
  if (err instanceof ApiError) {
    // Friendlier copy only for the APPLY 403 (the PM/TECH_LEAD gate). Every
    // other status — and any preview 403 (tenant membership) — falls back to
    // the server's own message, which is already user-meaningful: 400 (bad
    // token shape) / 404 (no tokens artefact) / 409 (stale hash or all-excluded)
    // / 5xx / preview "not a member of this tenant".
    if (isApply && err.status === 403) {
      return "You don't have permission to apply these changes — applying token refinements requires a Product Manager or Tech Lead role on this project.";
    }
    return err.message;
  }
  return err instanceof Error ? err.message : 'Something went wrong importing the tokens.';
}
